/**
 * « Réclamations fournisseur » on the pharmacy plane — /achats/reclamations, /nouvelle, /:id and the
 * §B2 drawer of S1 / S3 (design/ux/03_avoirs_bc_reclamations.md §5.1-§5.7, §6.5, §6.6; refonte
 * WP-35). Every sentence of the journal, the composer, the document, its dialogs and the drawer,
 * verbatim from the design where it gives one, once. Vouvoiement, sentence case, no raw enum value,
 * no English. The server's own refusals (409 / 422) are shown as the server words them.
 */
import { LIBELLES_CANAL_RECLAMATION, RECLAMATION_BORNES as B, VUE_JOURNAL_RECLAMATION as V } from "@pharmakon/shared/constants/reclamations.js";
import { LIBELLES_TYPE_AVOIR } from "@pharmakon/shared/constants/avoirs.js";

const pluriel = (n, un, plusieurs) => (n > 1 ? plusieurs : un);
const mo = (octets) => String(Math.round(octets / (1024 * 1024)));

/** ux/03 §5.4 — the journal. */
export const M_RJ = Object.freeze({
  titre: "Journal des réclamations",
  sousTitre: "Réclamations adressées à vos fournisseurs — manquants, casse, péremption, erreurs, prix — et leurs réponses",
  recherche: "Rechercher : N°, objet, produit, lot, facture …",
  grille: "Réclamations fournisseur",
  nouvelle: "Nouvelle réclamation",
  vues: Object.freeze({
    [V.OUVERTES]: "Ouvertes",
    [V.A_TRAITER]: "À traiter par vous",
    [V.CLOTUREES]: "Clôturées",
    [V.TOUTES]: "Toutes",
  }),
  vuesLabel: "Vue",
  filtres: Object.freeze({
    fournisseur: "Fournisseur",
    motif: "Motif",
    periode: "Période",
    canal: "Canal",
    avoir: "Avec avoir généré",
    etat: "État",
  }),
  colonnes: Object.freeze({
    numero: "N°",
    date: "Date",
    fournisseur: "Fournisseur",
    facture: "Facture",
    motif: "Motif",
    objet: "Objet",
    montant: "Montant réclamé",
    etat: "État",
    activite: "Activité",
  }),
  brouillonDu: (date) => `Brouillon du ${date}`,
  nonLu: "Nouvelle activité du fournisseur",
  aTraiter: "À traiter par vous",
  propositionEnAttente: "Proposition d'avoir en attente de votre réponse",
  ligneTitre: ({ facture, activite }) => [facture ? `Facture ${facture}` : null, activite ? `Dernière activité le ${activite}` : null].filter(Boolean).join(" · "),
  pied: ({ nombre, montant, ouvertes }) =>
    `${nombre} ${pluriel(nombre, "réclamation", "réclamations")} · montant réclamé ${montant} DA · dont ouvertes ${ouvertes} DA`,
  piedCompact: ({ nombre, montant }) => `${nombre} ${pluriel(nombre, "réclamation", "réclamations")} · ${montant} DA`,
  vide: "Aucune réclamation pour l'instant.",
  videAide: "Signalez un manquant, un produit cassé ou périmé, une erreur ou un prix à un fournisseur (F3).",
  videFiltre: "Aucune réclamation ne correspond à ces critères.",
  erreur: "Le journal n'a pas pu être chargé.",
  interdit: "Vous n'avez pas les droits pour consulter les réclamations. Contactez le propriétaire de l'officine.",
  suppressionRefusee: "Seul un brouillon sans numéro se supprime ; ouvrez la réclamation pour la retirer.",
  supprimer: Object.freeze({
    titre: (n) => `Supprimer le ${n.toLowerCase()} ?`,
    texte: "Le brouillon n'a jamais été envoyé ; il sera supprimé définitivement avec ses pièces jointes.",
    confirmer: "Supprimer le brouillon",
    revenir: "Revenir",
    succes: "Brouillon supprimé.",
  }),
});

/** ux/03 §5.3 — the composer (drawer 720 px, or the full page /achats/reclamations/nouvelle). */
export const M_RC = Object.freeze({
  titre: "Nouvelle réclamation",
  titreFournisseur: (nom) => `Nouvelle réclamation — ${nom}`,
  titreBrouillon: (date) => `Réclamation — brouillon du ${date}`,
  titreBrouillonNumero: (numero) => `Réclamation ${numero} — brouillon`,
  canal: Object.freeze({
    PLATEFORME: "● Connecté à Pharmakon · la réclamation arrive dans son espace Pharmakon",
    EMAIL: (masque) => `○ Hors plateforme · envoi par e-mail${masque ? ` (${masque})` : ""} ou impression`,
    IMPRESSION: "○ Hors plateforme · aucune adresse e-mail : impression puis remise au fournisseur",
    sansFournisseur: "Choisissez le fournisseur : le canal d'envoi en dépend.",
  }),
  champs: Object.freeze({
    fournisseur: "Fournisseur",
    facture: "Facture concernée",
    motif: "Motif",
    objet: "Objet",
    lignes: "Lignes concernées",
    montant: "Montant réclamé",
    description: "Description",
    pieces: "Pièces jointes",
  }),
  facturePlaceholder: "Aucune — rechercher par N° de facture",
  factureAucune: "Aucune facture",
  factureOption: ({ libelle, interne, date }) => [libelle, interne != null ? `n° ${interne}` : null, date].filter(Boolean).join(" · "),
  facturesVide: "Aucune facture enregistrée de ce fournisseur ne correspond.",
  facturesErreur: "Les factures du fournisseur n'ont pas pu être lues.",
  factureSaisieEnCours:
    "Pour rattacher la réclamation à cette facture, enregistrez d'abord la facture (brouillon ou validée).",
  objetParDefaut: (motif, facture) => (facture ? `${motif} — facture ${facture}` : motif),
  lignesFacture: "Lignes de la facture",
  lignesSansFacture: "Choisissez la facture concernée pour y prendre des lignes.",
  lignesVide: "Aucune ligne : la réclamation porte sur la facture ou sur le fournisseur.",
  colonnes: Object.freeze({
    produit: "Produit",
    lot: "Lot",
    factRecue: "Fact. / Reçue",
    quantite: "Qté réclamée",
    motif: "Motif",
    montant: "Montant",
  }),
  retirerLigne: (produit) => `Retirer ${produit} de la réclamation`,
  quantiteLabel: (produit) => `Quantité réclamée — ${produit}`,
  // refonte WP-89 (AUD-CP-08) — the visible « Qté réclamée » field of each line: its bound, said under it
  quantiteAide: (facturee) => (facturee != null ? `Au plus ${facturee} (quantité facturée).` : "Au moins 1."),
  motifLigneLabel: (produit) => `Motif de la ligne — ${produit}`,
  motifLigneDefaut: "Motif principal",
  montantCalcule: (m) => `${m} DA (calculé depuis les lignes)`,
  montantAide: "Montant en DA, deux décimales au plus — saisi seulement quand la réclamation n'a pas de ligne chiffrée.",
  descriptionAide: (requise) => (requise ? `Obligatoire pour le motif « Autre » (au moins ${B.description_min} caractères).` : "Facultatif — ce que le fournisseur doit savoir."),
  piecesBouton: "Joindre un fichier",
  piecesAide: `JPEG, PNG ou PDF · ${mo(B.pj_max_taille_octets)} Mo par fichier · ${B.pj_max_fichiers} fichiers au plus`,
  piecesDeposer: "ou déposez les fichiers ici",
  retirerPiece: (nom) => `Retirer ${nom}`,
  pieceEnAttente: "joint à l'enregistrement",
  enregistrer: "Enregistrer le brouillon",
  imprimer: "Imprimer",
  envoyer: Object.freeze({
    PLATEFORME: "Envoyer au fournisseur",
    EMAIL: "Envoyer par e-mail…",
    IMPRESSION: "Imprimer et marquer envoyée",
  }),
  supprimer: "Supprimer le brouillon",
  supprimerTitre: "Supprimer le brouillon ?",
  enregistre: "Brouillon enregistré.",
  enregistreAvecNumero: (numero) => `Brouillon ${numero} enregistré.`,
  echec: "La réclamation n'a pas pu être enregistrée.",
  piecesEchec: "Les pièces jointes n'ont pas été ajoutées",
  erreurs: Object.freeze({
    fournisseur: "Choisissez le fournisseur.",
    motif: "Choisissez le motif de la réclamation.",
    objet: "Saisissez l'objet de la réclamation.",
    objetLong: `L'objet tient en ${B.objet_max} caractères au plus.`,
    description: `Décrivez le problème (au moins ${B.description_min} caractères).`,
    descriptionLongue: `La description tient en ${B.description_max} caractères au plus.`,
    quantite: "La quantité réclamée doit être au moins 1.",
    quantiteFacturee: (q) => `La quantité réclamée ne peut pas dépasser la quantité facturée (${q}).`,
    montant: "Montant en DA strictement positif, deux décimales au plus.",
    lignesMax: `${B.lignes_max} lignes au maximum.`,
    pieceTaille: `Ce fichier dépasse ${mo(B.pj_max_taille_octets)} Mo.`,
    pieceType: "Format non accepté : joignez une image (JPEG, PNG) ou un PDF.",
    piecesNombre: `${B.pj_max_fichiers} pièces jointes au maximum.`,
    pieceVide: "Ce fichier est vide.",
  }),
  abandonTitre: "Abandonner la saisie ?",
  abandonTexte: "La réclamation en cours de saisie sera perdue.",
  abandonContinuer: "Continuer la saisie",
  abandonConfirmer: "Abandonner",
  changerFournisseurTitre: "Changer de fournisseur ?",
  changerFournisseurTexte: "Une réclamation ne concerne qu'un seul fournisseur : la facture et les lignes choisies seront retirées.",
  changerFournisseur: "Changer de fournisseur",
  garderFournisseur: (nom) => `Garder ${nom}`,
  chargement: "Chargement de la réclamation…",
  verrouille: "Le fournisseur est celui de la facture : il ne se change pas ici.",
});

/** ux/03 §5.3 « Lignes de la facture » — the multi-select picker. */
export const M_RLF = Object.freeze({
  titre: (facture) => `Lignes de la facture ${facture}`,
  sousTitre: "Espace coche une ligne ; Entrée ajoute les lignes cochées à la réclamation.",
  grille: "Lignes de la facture",
  colonnes: Object.freeze({ produit: "Produit", lot: "Lot", fact: "Qté fact.", recue: "Reçue", proposee: "Qté proposée", pu: "PU net HT" }),
  dejaDansReclamation: "Déjà dans la réclamation",
  ajouter: (n) => (n > 0 ? `Ajouter ${n} ${pluriel(n, "ligne", "lignes")}` : "Ajouter les lignes"),
  annuler: "Annuler",
  vide: "Cette facture n'a pas de ligne.",
  erreur: "Les lignes de la facture n'ont pas pu être lues.",
  chargement: "Lecture des lignes…",
});

/** ux/03 §5.3 / §5.6 — sending (ConfirmDialog, e-mail, print then « remise ? »). */
export const M_RENVOI = Object.freeze({
  confirmerTitre: (fournisseur) => `Envoyer la réclamation à ${fournisseur} ?`,
  confirmerTexte: "Son contenu ne pourra plus être modifié ; vous pourrez ajouter des messages et des pièces jointes.",
  revenir: "Revenir",
  envoyer: "Envoyer",
  envoyee: (numero, fournisseur) => `Réclamation ${numero} envoyée à ${fournisseur}.`,
  emailEnvoye: (numero) => `Réclamation ${numero} envoyée par e-mail.`,
  remiseTitre: "La réclamation a-t-elle été remise au fournisseur ?",
  remiseTexte: (numero) => `Une fois la lettre ${numero} remise (en main propre, par fax ou par courrier), marquez-la envoyée : le suivi commence à cette date.`,
  remiseOui: "Oui, marquer comme envoyée",
  remiseNon: "Pas encore",
  marqueeEnvoyee: (numero) => `Réclamation ${numero} marquée envoyée.`,
  impressionEchec: "La lettre de réclamation n'a pas pu être ouverte.",
  numeroEchec: "Le numéro de la réclamation n'a pas pu être attribué.",
});

/**
 * Refonte WP-89 (CP §4.2, D-36; WP-88 contract) — « Effet sur le stock » of « Envoyer » / F9 and of
 * « Déduire du stock… » after the send: each product line the motif deducts or asks, its two options
 * (the server's words), the lot when the line has none, why « maintenant » is unavailable.
 */
export const M_RDED = Object.freeze({
  titreSection: "Effet sur le stock",
  aide: "Pour chaque produit : déduire ses unités du stock dès l'envoi, ou lors de la création de l'avoir (choix par défaut).",
  ligne: ({ quantite, produit, lot }) => `${quantite} × ${produit}${lot ? ` — lot ${lot}` : ""}`,
  choix: (produit) => `Effet sur le stock — ${produit}`,
  lotAChoisir: "lot à choisir",
  lot: (produit) => `Lot de ${produit} à déduire`,
  lotOption: (numero, peremption, payees) => `${numero}${peremption ? ` · péremption ${peremption}` : ""} · ${payees} ${pluriel(payees, "unité payée disponible", "unités payées disponibles")}`,
  lotRequis: (produit) => `Choisissez le lot de ${produit} à déduire du stock.`,
  raisons: Object.freeze({
    STOCK_INSUFFISANT: (n) => `Indisponible : ${n ?? 0} ${pluriel(n ?? 0, "unité payée disponible", "unités payées disponibles")} dans le lot (les UG ne se déduisent pas).`,
    LOT_REQUIS: "Indisponible : aucun lot de ce produit n'a d'unités payées en stock.",
    DEJA_DEDUITE: "Déjà déduit du stock.",
    SANS_OBJET: "Ce motif ne déduit pas de stock.",
  }),
  sansEffet: (n) => `${n} ${pluriel(n, "ligne sans effet sur le stock", "lignes sans effet sur le stock")} (motif sans déduction).`,
  chargement: "Lecture des lignes et de leur stock…",
  erreur: (message) => `L'effet sur le stock n'a pas pu être lu${message ? ` (${message})` : ""} : sans choix, la déduction se fera à la création de l'avoir.`,
  apresTitre: (numero) => `Déduire du stock — réclamation ${numero}`,
  apresTexte: "Les lignes passées à « Déduire du stock maintenant » sortent de leur lot maintenant ; les autres seront déduites à la création de l'avoir.",
  apresConfirmer: "Déduire du stock",
  apresAucune: "Choisissez « Déduire du stock maintenant » sur au moins une ligne.",
  apresAucuneLigne: "Aucune ligne n'attend plus de déduction du stock.",
  succes: (n) => `Stock déduit pour ${n} ${pluriel(n, "ligne", "lignes")}.`,
  emailEchec: (message) => `La réclamation est envoyée, mais la déduction du stock a été refusée : ${message} Utilisez « Déduire du stock… » sur la réclamation.`,
  reintegrerTitre: (numero) => `Réintégrer au stock — réclamation ${numero}`,
  reintegrerTexte: "Les unités que cette réclamation a déduites du stock, et qu'aucun avoir ne reprend, reviennent dans leur lot.",
  reintegrerConfirmer: "Réintégrer au stock",
  reintegrerSucces: "Stock réintégré.",
});

/** ux/03 §5.5 — the réclamation document /achats/reclamations/:id. */
export const M_RD = Object.freeze({
  titre: (numero) => `Réclamation ${numero}`,
  sections: Object.freeze({
    objet: "Objet",
    lignes: "Lignes",
    description: "Description",
    pieces: "Pièces jointes",
    historique: "Historique et échanges",
    liens: "Liens",
  }),
  onglets: Object.freeze({ detail: "Détail", historique: (n) => `Historique (${n})` }),
  champs: Object.freeze({
    motif: "Motif",
    facture: "Facture",
    efacture: "E-facture",
    bonCommande: "Bon de commande",
    bonRetour: "Bon de retour",
    montant: "Montant réclamé",
    fournisseur: "Fournisseur",
    canal: "Canal",
    creePar: "Saisie par",
    destinataire: "Envoyée à",
  }),
  colonnes: Object.freeze({
    produit: "Produit",
    lot: "Lot",
    factRecue: "Fact. / Reçue",
    reclamee: "Réclamé",
    accordee: "Accordé",
    montant: "Montant",
    // refonte WP-89 (D-36) — the line's stock: deducted « maintenant », at the avoir's creation
    stock: "Stock",
  }),
  /** Refonte WP-89 — the stock state of a document line (the server's label, the units, when, given back). */
  stockLigne: Object.freeze({
    deduit: (libelle, quantite, date) => `${libelle} : ${quantite}${date ? ` le ${date}` : ""}`,
    reintegree: (n) => `${n} ${pluriel(n, "réintégrée", "réintégrées")}`,
  }),
  factRecue: (f, r) => `${f ?? "—"} / ${r ?? "—"}`,
  statut: Object.freeze({
    BROUILLON: "Brouillon — le numéro est attribué à l'envoi.",
    BROUILLON_NUMERO: (numero) => `Brouillon — numéro ${numero} réservé pour la lettre ou l'e-mail.`,
    ENVOYEE: (date, canal) => `Envoyée le ${date} (${canal}).`,
    EN_COURS_FOURNISSEUR: (fournisseur, date) => `Prise en charge par ${fournisseur} le ${date}.`,
    EN_COURS_CONSIGNEE: (date) => `En cours de traitement chez le fournisseur depuis le ${date} (réponse consignée).`,
    RESOLUE: (date, resolution) => `Résolue le ${date} : ${resolution}`,
    REJETEE: (date, motif) => `Rejetée le ${date} : ${motif}`,
    CLOTUREE: (date) => `Clôturée le ${date}.`,
    RETIREE: (date, motif) => `Retirée le ${date} : ${motif}`,
  }),
  proposition: ({ fournisseur, montant, lignes, message }) =>
    `${fournisseur} propose un avoir${montant ? ` de ${montant} DA` : ""}${lignes ? ` (${lignes} ${pluriel(lignes, "ligne", "lignes")})` : ""}${message ? ` — « ${message} »` : ""}`,
  avoirGenere: (numero, date) => `Avoir ${numero} généré le ${date}.`,
  avoirAnnule: (numero) => `L'avoir ${numero} a été annulé.`,
  clotureSansAvoir: "Aucun avoir n'a été généré : une fois clôturée, la réclamation ne pourra plus en générer.",
  aucuneLigne: "Aucune ligne : la réclamation porte sur la facture ou sur le fournisseur.",
  aucunePiece: "Aucune pièce jointe.",
  aucunEvenement: "Aucun événement.",
  piece: (nom, taille) => `${nom} (${taille})`,
  pieceOuvrir: (nom) => `Ouvrir ${nom}`,
  pieceSupprimer: (nom) => `Retirer ${nom}`,
  pieceEchec: "La pièce jointe n'a pas pu être ouverte.",
  pieceCote: Object.freeze({ PHARMACIE: "jointe par la pharmacie", FOURNISSEUR: "jointe par le fournisseur" }),
  ajouterPieces: "Joindre un fichier",
  piecesAjoutees: (n) => `${n} ${pluriel(n, "pièce jointe ajoutée", "pièces jointes ajoutées")}.`,
  actions: Object.freeze({
    imprimer: "Imprimer",
    email: "Envoyer par e-mail…",
    relancer: "Relancer",
    relancerEmail: "Relancer par e-mail…",
    consigner: "Consigner la réponse…",
    retirer: "Retirer…",
    cloturer: "Clôturer",
    accepter: "Accepter la proposition",
    refuser: "Refuser et répondre",
    genererAvoir: "Générer l'avoir",
    editeurAvoir: "Ouvrir dans l'éditeur d'avoir",
    // refonte WP-89 (brief 2026-11-01 §2.4, K1 A5) — one editor entry (F9 unless « Accepter la proposition » is
    // offered), WP-83's one click second; the stock of the claimed lines after the send (WP-88's actions)
    creerAvoir: "Créer l'avoir",
    genererSansModifier: "Générer sans modifier",
    deduireStock: "Déduire du stock…",
    reintegrerStock: "Réintégrer au stock…",
    modifier: "Modifier le brouillon",
    journal: "Journal des réclamations",
    nouvelleLiee: "Nouvelle réclamation liée",
  }),
  raisons: Object.freeze({
    droits: "Votre rôle ne permet pas cette action.",
    relanceTot: (date) => `Une relance a déjà été envoyée : prochaine relance possible le ${date}.`,
    connecte: "Ce fournisseur est connecté : c'est lui qui donne la suite dans son espace Pharmakon.",
  }),
  relancee: "Relance envoyée.",
  cloturee: (numero) => `Réclamation ${numero} clôturée.`,
  propositionAcceptee: "Proposition acceptée : la réclamation est résolue. Vous pouvez générer l'avoir.",
  chargement: "Chargement de la réclamation…",
  erreur: "La réclamation n'a pas pu être chargée.",
  introuvable: "Cette réclamation est introuvable dans votre officine.",
  interdit: "Vous n'avez pas les droits pour consulter les réclamations. Contactez le propriétaire de l'officine.",
});

/** ux/03 §5.5 — the composer of the right column (message / note / consigned answer). */
export const M_RFIL = Object.freeze({
  placeholderConnecte: "Écrire au fournisseur…",
  placeholderConsigne: "Note ou réponse du fournisseur…",
  label: "Message",
  note: "Note interne",
  noteAide: "Visible de votre officine seulement.",
  consigner: "Réponse du fournisseur (consigner)",
  envoyer: "Envoyer",
  enregistrer: "Enregistrer",
  raccourci: "Ctrl+Entrée",
  envoye: "Message envoyé au fournisseur.",
  noteEnregistree: "Note enregistrée.",
  vide: "Le message est vide.",
  long: `Le message tient en ${B.message_max} caractères au plus.`,
  ferme: "La réclamation est clôturée : plus aucun message ne s'y ajoute.",
});

/** « 2 × AMOXICILLINE 1 G (lot A7781), 1 × SPASFON » — the lines of a stock event. */
const unitesStock = (lignes) =>
  (Array.isArray(lignes) ? lignes : []).map((l) => `${l.quantite} × ${l.produit}${l.numero_lot ? ` (lot ${l.numero_lot})` : ""}`).join(", ");

/** The timeline wording (ux/03 §5.5, P-9). */
export const M_RFIL_EVENEMENTS = Object.freeze({
  CREATION: ({ auteur }) => `Créée${auteur ? ` par ${auteur}` : ""}`,
  ENVOI: ({ canal, destinataire, auteur }) =>
    `Envoyée ${canal === "EMAIL" ? `par e-mail${destinataire ? ` à ${destinataire}` : ""}` : canal === "PLATEFORME" ? "par la plateforme" : "sur papier (lettre remise)"}${auteur ? ` par ${auteur}` : ""}`,
  ENVOI_ECHEC: ({ message }) => `Échec de l'envoi de l'e-mail${message ? ` : ${message}` : ""}`,
  RELANCE: ({ canal }) => (canal === "EMAIL" ? "Relance envoyée par e-mail" : "Relance envoyée"),
  PRISE_EN_CHARGE: ({ fournisseur }) => `Prise en charge par ${fournisseur}`,
  MESSAGE_PHARMACIE: ({ auteur }) => `Message${auteur ? ` de ${auteur}` : ""}`,
  NOTE_INTERNE: ({ auteur }) => `Note interne${auteur ? ` de ${auteur}` : ""}`,
  MESSAGE_FOURNISSEUR: ({ fournisseur }) => `Message de ${fournisseur}`,
  PIECE_JOINTE: ({ n, cote, fournisseur }) =>
    `${n} ${pluriel(n, "pièce jointe ajoutée", "pièces jointes ajoutées")}${cote === "FOURNISSEUR" ? ` par ${fournisseur}` : ""}`,
  PROPOSITION: ({ fournisseur, montant }) => `${fournisseur} propose un avoir${montant ? ` de ${montant} DA` : ""}`,
  PROPOSITION_ACCEPTEE: () => "Proposition acceptée",
  PROPOSITION_REFUSEE: () => "Proposition refusée",
  REPONSE_CONSIGNEE: ({ statut }) => `Réponse du fournisseur consignée${statut ? ` — ${statut}` : ""}`,
  RESOLUTION: ({ fournisseur }) => `Acceptée par ${fournisseur}`,
  REJET: ({ fournisseur }) => `Rejetée par ${fournisseur}`,
  AVOIR_GENERE: ({ numero, montant }) => `Avoir ${numero ?? ""} généré${montant ? ` (${montant} DA)` : ""}`,
  CLOTURE: () => "Clôturée",
  RETRAIT: () => "Réclamation retirée",
  // refonte WP-88 (D-36, A-83) — the claimed units out of / back into their lot (pharmacy only)
  DEDUCTION_STOCK: ({ lignes, auteur }) => `Stock déduit : ${unitesStock(lignes)}${auteur ? ` — par ${auteur}` : ""}`,
  RESTITUTION_STOCK: ({ lignes, auteur }) => `Stock réintégré : ${unitesStock(lignes)}${auteur ? ` — par ${auteur}` : ""}`,
  interne: "interne",
});

/** OP-03-28 — « Consigner la réponse du fournisseur » (non-connected fournisseur). */
export const M_RCONSIGNER = Object.freeze({
  titre: "Consigner la réponse du fournisseur",
  sousTitre: "La réponse reçue par e-mail, téléphone ou courrier — le fournisseur n'a pas d'espace Pharmakon.",
  issue: "Réponse du fournisseur",
  issues: Object.freeze({
    EN_COURS: "En cours de traitement",
    RESOLUE: "Acceptée — réclamation résolue",
    REJETEE: "Refusée — réclamation rejetée",
  }),
  note: "Ce qu'a répondu le fournisseur",
  noteAide: "Elle figure dans l'historique (interne à votre officine).",
  noteRequise: "Notez la réponse du fournisseur.",
  enregistrer: "Consigner",
  revenir: "Revenir",
  succes: Object.freeze({
    EN_COURS: "Réponse consignée : la réclamation est en cours.",
    RESOLUE: "Réponse consignée : la réclamation est résolue. Vous pouvez générer l'avoir.",
    REJETEE: "Réponse consignée : la réclamation est rejetée.",
  }),
});

/** OP-03-27 — « Retirer la réclamation » (motif) → Clôturée. */
export const M_RRETIRER = Object.freeze({
  titre: (numero) => `Retirer la réclamation ${numero} ?`,
  texte: "Elle est clôturée avec son motif ; le numéro reste dans le journal. Le fournisseur connecté en est informé.",
  motif: "Motif du retrait",
  motifAide: `Au moins ${B.motif_min} caractères — il figure dans l'historique.`,
  motifCourt: `Indiquez le motif du retrait (au moins ${B.motif_min} caractères).`,
  confirmer: "Retirer la réclamation",
  revenir: "Revenir",
  succes: (numero) => `Réclamation ${numero} retirée.`,
});

/** ux/03 §5.5 — « Refuser et répondre » to the fournisseur's proposition. */
export const M_RREFUS = Object.freeze({
  titre: "Refuser la proposition",
  texte: "Expliquez au fournisseur pourquoi : la réclamation reste en cours et il peut vous faire une autre proposition.",
  message: "Votre réponse au fournisseur",
  requis: "Expliquez au fournisseur pourquoi vous refusez sa proposition.",
  confirmer: "Refuser la proposition",
  revenir: "Revenir",
  succes: "Proposition refusée : votre réponse est envoyée au fournisseur.",
});

/** ux/03 §5.7 — « Générer l'avoir » (one click + ConfirmDialog). */
export const M_RAVOIR = Object.freeze({
  titre: (numero) => `Générer l'avoir pour la réclamation ${numero} ?`,
  // refonte WP-83 (EXE-104) — `type` is the long label (« Avoir produits », « Avoir financier — … »), never prefixed again
  resume: ({ type, fournisseur, lignes, ttc }) =>
    `${type} · ${fournisseur}${lignes ? ` · ${lignes} ${pluriel(lignes, "ligne", "lignes")}` : ""} · ${ttc} DA`,
  solde: (avant, apres) => `Solde fournisseur : ${avant} → ${apres}.`,
  soldeSens: Object.freeze({
    A_PAYER: (m) => `${m} DA à payer`,
    CREDIT: (m) => `crédit en votre faveur ${m} DA`,
    A_JOUR: "à jour",
  }),
  source: Object.freeze({ PROPOSITION: "Montants de la proposition acceptée.", RECLAMATION: "Montants réclamés, acceptés par le fournisseur." }),
  nonRepris: (n) => `${n} ${pluriel(n, "ligne n'est pas reprise", "lignes ne sont pas reprises")} (sans ligne de facture ou sans produit) : complétez dans l'éditeur d'avoir si besoin.`,
  sansType: "Ce motif n'entraîne pas d'avoir par défaut.",
  choisirType: "Choisir un type d'avoir",
  // the « long » labels of the avoir kinds, one definition (refonte WP-81 — CP §8.3; PK-BUG-15)
  types: Object.freeze(Object.fromEntries(Object.entries(LIBELLES_TYPE_AVOIR).map(([t, l]) => [t, l.long]))),
  colonnes: Object.freeze({ produit: "Produit", lot: "Lot", quantite: "Qté", montant: "Montant" }),
  chargement: "Préparation de l'avoir…",
  erreur: "L'aperçu de l'avoir n'a pas pu être préparé.",
  generer: "Générer l'avoir",
  editeur: "Ouvrir dans l'éditeur",
  // refonte WP-83 (D-32) — the state the avoir was written in (« En instance » / « Confirmé »)
  succes: (numero, statut = null) => `Avoir ${numero} généré${statut ? ` (${statut.toLowerCase()})` : ""} et lié à la réclamation.`,
});

/** ux/03 §5.2.1 — the Réclamations drawer of S1 / S3 (§B2). */
export const M_RTIROIR = Object.freeze({
  titre: (fournisseur) => `Réclamations — ${fournisseur}`,
  compteurs: ({ ouvertes, cloturees }) =>
    `${ouvertes} ${pluriel(ouvertes, "ouverte", "ouvertes")} · ${cloturees} ${pluriel(cloturees, "clôturée", "clôturées")}`,
  grille: "Réclamations du fournisseur",
  colonnes: Object.freeze({ numero: "N°", date: "Date", motif: "Motif", facture: "Facture", etat: "État" }),
  nouvelle: "Nouvelle réclamation",
  preparation: "Préparation de la réclamation…",
  ouvrir: "Entrée : ouvrir",
  retour: "‹ Retour à la liste",
  journal: "Ouvrir le journal",
  pagePleine: "Ouvrir la page",
  vide: "Aucune réclamation pour ce fournisseur.",
  erreur: "Les réclamations du fournisseur n'ont pas pu être lues.",
  plus: (n) => `${n} réclamations — les plus récentes ; le journal les montre toutes.`,
});

/** ux/02 §4.6 / ux/03 §5.2.1 — the « Réclamations · n » button of S1 and S3 (OP-03-35). */
export const M_RB2 = Object.freeze({
  bouton: "Réclamations",
  boutonCompte: (n) => `Réclamations · ${n}`,
  aide: ({ ouvertes, resolues, cloturees }) =>
    `${ouvertes} ${pluriel(ouvertes, "ouverte", "ouvertes")} · ${resolues} ${pluriel(resolues, "résolue", "résolues")} · ${cloturees} ${pluriel(cloturees, "clôturée", "clôturées")}`,
  aideIndisponible: "Réclamations du fournisseur",
  sansFournisseur: "Choisissez d'abord le fournisseur.",
  signaler: "Signaler une réclamation",
  creer: (n) => `Créer une réclamation (${n} ${pluriel(n, "ligne", "lignes")})`,
  selection: "Lignes sélectionnées",
  ouverte: (numero) => `Réclamation ouverte : ${numero}`,
  voir: "Voir la réclamation",
  brouillon: "brouillon",
});

/** ux/03 §6.6 — the journal printed A4 landscape. */
export const M_IMPRESSION_RJ = Object.freeze({
  titre: "Journal des réclamations",
  vue: (v) => `Vue : ${v}`,
  periode: (du, au) => (du || au ? `Période : ${du ?? "…"} au ${au ?? "…"}` : "Toutes périodes"),
  edite: (date, heure) => `Édité le ${date} à ${heure} par Pharmakon`,
  tronque: (n, total) => `${n} réclamations imprimées sur ${total} : affinez les filtres pour la liste complète.`,
  echec: "Le journal n'a pas pu être imprimé.",
  vide: "Aucune réclamation à imprimer pour ces critères.",
});

/** The channel words (A-19), for the screens that print them. */
export const LIBELLE_CANAL = (canal) => LIBELLES_CANAL_RECLAMATION[canal] ?? "—";
